import React from 'react';
import { CheckCircle, XCircle, Truck, RotateCcw, Download, Printer } from 'lucide-react';
import ProBadge from '../b2b/ProBadge';
import CommandeLigneDetails from '../commande/CommandeLigneDetails';
import { getCommandeLigneDesignation as getLigneDesignation } from '../../utils/commandeLigneDetailViewModel';
import { isCommandeLivreurModifiable, formatLivreurNom } from '../../utils/commandeStatuts';
import { resolveCommandeDetailTotals } from '../../utils/commandeDetailTotals';
import { resolveLignePrixPromo, estimateIndisponibleRefundDelta } from '../../utils/commandeLignePrixPromo';
import { isFactureTelechargeable } from '../../utils/commandeFactureDisponible';
import { buildReductionDisplayLines } from '../../utils/commandePricingDisplay';
import { getRdvPaymentView, formatEuro } from '../../utils/rdvPaymentDisplay';
import {
  buildTenderBreakdown,
  hasGiftTender,
  isOnlinePaidPriceLocked,
  isVariableWeightLine,
  commandeHasVariableWeightLines,
  PRICE_LOCK_MESSAGE,
  VARIABLE_WEIGHT_LOCK_EXCEPTION_HINT,
} from '../../utils/paiementTenderBreakdown';
import { estimerPrixDepuisPoids } from '../../utils/variableWeightEstimate';
import { getCommandeFinancier, getPendingComplementCheckoutUrl } from '../../utils/commandeFinanceDisplay';
import { resolveLivraisonCommandeDisplay } from '../../utils/livraisonCommandeDisplay';
import { canShowGenerateExpeditionButton } from '../../utils/expeditionShippingOrderUi';
import { canShowExpeditionLabelActions, expeditionLabelStatusMessage } from '../../utils/expeditionShippingLabelUi';
import { canShowExpeditionMerchantHandoffButton } from '../../utils/expeditionMerchantHandoffUi';
import {
  canShowExpeditionTrackingPanel,
  canSyncExpeditionTracking,
  expeditionCarrierFollowLinkLabel,
  expeditionDraftPreparationMessage,
  expeditionShipmentStatusLabel,
} from '../../utils/expeditionShipmentTrackingUi';

/**
 * Modal détail commande produit — extrait de CommercantCommandes (présentation inchangée).
 */
export default function MesCommandesOrderDetailModal({
  commande,
  onClose,
  isUpdating = false,
  getNextStatus,
  getStatusDisplay,
  getNextStatusLabel,
  openStatusConfirmation,
  openRefundModal,
  hasStripePaymentIntent,
  getCommandeSoldeRemboursable,
  isCommandePaiementEnLigne,
  isRendezVous,
  reprintLoading,
  onReprintTicket,
  refundLoading,
  factureLoading,
  onDownloadFacture,
  avoirLoadingId,
  onDownloadAvoir,
  livreursDisponibles = [],
  livreurHistorique,
  selectedLivreurId,
  onSelectedLivreurIdChange,
  assigningLivreur,
  onAssignerLivreur,
  expeditionCapabilities,
  generatingShippingOrder,
  onOpenGenerateExpeditionConfirmation,
  expeditionLabelUi,
  expeditionLabelPolling,
  onRefreshExpeditionLabelStatus,
  onDownloadExpeditionLabel,
  merchantHandoffSubmitting,
  onOpenExpeditionHandoffConfirmation,
  expeditionTrackingSyncing,
  onSyncExpeditionTracking,
  poidsComplementUrl,
  poidsDrafts,
  onPoidsDraftsChange,
  indisponibleMessage,
  markingIndisponible,
  onOpenLigneIndisponibleConfirmation,
  statutsLigneIndisponible,
  parseSavReference,
  formatDateTime,
  onApplyPoidsReels,
  applyingPoids,
  onSavePoidsAjustement,
  savingPoids: savingPoidsProp,
  poidsError,
  poidsMessage,
}) {
  if (!commande) return null;

  const setPoidsDrafts = onPoidsDraftsChange;
  const handleSavePoidsAjustement = onSavePoidsAjustement ?? onApplyPoidsReels;
  const savingPoids = savingPoidsProp ?? applyingPoids;
  const handleReprintTicket = onReprintTicket;
  const handleDownloadFacture = onDownloadFacture;
  const handleDownloadAvoir = onDownloadAvoir;
  const handleAssignerLivreur = onAssignerLivreur;
  const openGenerateExpeditionConfirmation = onOpenGenerateExpeditionConfirmation;
  const refreshExpeditionLabelStatus = onRefreshExpeditionLabelStatus;
  const handleDownloadExpeditionLabel = onDownloadExpeditionLabel;
  const openExpeditionHandoffConfirmation = onOpenExpeditionHandoffConfirmation;
  const handleSyncExpeditionTracking = onSyncExpeditionTracking;
  const openLigneIndisponibleConfirmation = onOpenLigneIndisponibleConfirmation;
  const STATUTS_LIGNE_INDISPONIBLE = statutsLigneIndisponible;
  const setSelectedLivreurId = onSelectedLivreurIdChange;

    const detailIsPosComptoir = commande.canalVente === 'POS' && commande.modeRetrait === 'comptoir';
    const detailNextStatus = detailIsPosComptoir ? null : getNextStatus(commande.statut, commande.modeRetrait, commande);
    const statusDisplay = getStatusDisplay(commande);
    const soldeRemboursable = getCommandeSoldeRemboursable(commande);
    const stripePiOk = hasStripePaymentIntent(commande);
    const giftTenderOk = hasGiftTender(commande);
    const tendersDetail = buildTenderBreakdown(commande?.paiements || [], {
      modePaiement: commande?.modePaiement,
    });
    const priceLockedOnline = isOnlinePaidPriceLocked(commande);
    const canRefundOnline =
      !detailIsPosComptoir &&
      commande.canalVente !== 'POS' &&
      isCommandePaiementEnLigne(commande) &&
      commande.statutPaiement === 'VALIDE' &&
      soldeRemboursable > 0 &&
      (stripePiOk || giftTenderOk);

    const assignedLivreur =
      commande.livreur ||
      livreurHistorique ||
      (commande.livreurId
        ? livreursDisponibles.find((l) => l.id === commande.livreurId)
        : null);
    const livreurNom = formatLivreurNom(assignedLivreur);
    const hasAssignedLivreur = !!(livreurNom || commande.livreurId);
    const livreurFigee = !isCommandeLivreurModifiable(commande.statut);

  return (
      <div
        className="fixed inset-0 bg-gray-600 bg-opacity-50 z-50 flex items-center justify-center p-4"
        onClick={onClose}
        role="presentation"
      >
        <div
          className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
          role="dialog"
          aria-modal="true"
        >
          <div className="p-6">
            <div className="flex justify-between items-center mb-6">
              <div>
                <h3 className="text-2xl font-bold text-gray-900">
                  Commande {commande.numero || `#${commande.id.slice(-8)}`}
                </h3>
                {commande.pricingMode === 'PRO' ? (
                  <div className="mt-2 flex items-center gap-3">
                    <ProBadge size="line" />
                    <span className="text-sm font-semibold text-indigo-800">Commande professionnelle</span>
                  </div>
                ) : null}
                <p className="text-sm text-gray-500 mt-1">
                  {new Date(commande.dateCommande).toLocaleDateString('fr-FR', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </p>
              </div>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600"
              >
                <span className="text-3xl">&times;</span>
              </button>
            </div>

            <div className="space-y-6">
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-semibold mb-2 text-gray-700">Statut de la commande</h4>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <span className={`inline-flex items-center px-4 py-2 text-sm font-semibold rounded-full ${statusDisplay.color}`}>
                    {statusDisplay.icon}
                    <span className="ml-2">{statusDisplay.label}</span>
                  </span>
                  <button
                    type="button"
                    onClick={handleReprintTicket}
                    disabled={reprintLoading}
                    className="inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Printer className="h-4 w-4 mr-2" />
                    {reprintLoading ? 'Impression…' : 'Réimprimer le ticket'}
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-6">
                <div className="border rounded-lg p-4">
                  <h4 className="font-semibold mb-3 text-gray-700">👤 Client</h4>
                  <p className="font-medium">
                    {commande.user?.prenom} {commande.user?.nom}
                  </p>
                  <p className="text-sm text-gray-600">{commande.user?.email}</p>
                  <p className="text-sm text-gray-600">{commande.user?.telephone}</p>
                </div>

                <div className="border rounded-lg p-4">
                  <h4 className="font-semibold mb-3 text-gray-700">🏪 Commerçant</h4>
                  <p className="font-medium">{commande.commercant?.nom}</p>
                  <p className="text-sm text-gray-600">{commande.commercant?.rue}</p>
                  <p className="text-sm text-gray-600">
                    {commande.commercant?.codePostal} {commande.commercant?.ville}
                  </p>
                </div>
              </div>

              {commande.pricingMode === 'PRO' && (() => {
                const snap = commande.professionalCustomerSnapshotJson || {};
                const addr = snap.adresseFacturation || {};
                return (
                  <div className="border rounded-lg p-4 bg-indigo-50/40">
                    <h4 className="font-semibold mb-3 text-gray-700">CLIENT PROFESSIONNEL</h4>
                    <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
                      <div>
                        <dt className="text-gray-500">Raison sociale</dt>
                        <dd className="font-medium text-gray-900">{snap.raisonSociale || '—'}</dd>
                      </div>
                      <div>
                        <dt className="text-gray-500">SIREN</dt>
                        <dd className="font-medium text-gray-900">{snap.siren || '—'}</dd>
                      </div>
                      {snap.siret ? (
                        <div>
                          <dt className="text-gray-500">SIRET</dt>
                          <dd className="font-medium text-gray-900">{snap.siret}</dd>
                        </div>
                      ) : null}
                      {snap.tvaIntracom ? (
                        <div>
                          <dt className="text-gray-500">TVA intra</dt>
                          <dd className="font-medium text-gray-900">{snap.tvaIntracom}</dd>
                        </div>
                      ) : null}
                      {snap.emailFacturation ? (
                        <div>
                          <dt className="text-gray-500">Email facturation</dt>
                          <dd className="font-medium text-gray-900">{snap.emailFacturation}</dd>
                        </div>
                      ) : null}
                      <div className="sm:col-span-2">
                        <dt className="text-gray-500">Adresse de facturation</dt>
                        <dd className="font-medium text-gray-900">
                          {[addr.rue, [addr.codePostal, addr.ville].filter(Boolean).join(' '), addr.pays]
                            .filter(Boolean)
                            .join(', ') || '—'}
                        </dd>
                      </div>
                    </dl>
                  </div>
                );
              })()}

              <div className="border rounded-lg p-4">
                <h4 className="font-semibold mb-3 text-gray-700">
                  {commande.modeRetrait === 'clickandcollect'
                    ? '📦 Click & Collect'
                    : commande.modeRetrait === 'expedition'
                      ? '📦 Expédition'
                      : '🚚 Livraison à domicile'}
                </h4>
                {commande.modeRetrait === 'clickandcollect' ? (
                  <div>
                    <p className="text-sm font-medium text-green-600">
                      ✅ {commande.retraitLibelleLieu || 'Retrait sur place'}
                    </p>
                    <p className="text-sm text-gray-600 mt-2">
                      {commande.retraitAdresseLigne1 || commande.commercant?.rue}
                      {commande.retraitAdresseLigne2 && (
                        <>
                          <br />
                          {commande.retraitAdresseLigne2}
                        </>
                      )}
                      <br />
                      {commande.retraitCodePostal || commande.commercant?.codePostal}{' '}
                      {commande.retraitVille || commande.commercant?.ville}
                    </p>
                    {commande.creneauRetrait && (
                      <p className="text-xs text-gray-500 mt-2">
                        Créneau : {commande.creneauRetrait}
                      </p>
                    )}
                  </div>
                ) : commande.modeRetrait === 'expedition' ? (
                  <div className="space-y-3">
                    {(() => {
                      const shipment = commande.shipment;
                      const shipmentStatus = String(shipment?.status || '').toUpperCase();
                      const isDraft = !shipment || shipmentStatus === 'DRAFT';
                      if (!isDraft) return null;
                      const showGenerate = canShowGenerateExpeditionButton(
                        commande,
                        expeditionCapabilities,
                      );
                      return (
                        <div className="rounded-lg p-3 border border-indigo-200 bg-indigo-50 space-y-2">
                          <p className="text-xs font-bold uppercase tracking-wide text-indigo-800">
                            Préparation
                          </p>
                          <p className="text-sm font-semibold text-indigo-900 mt-1">
                            {expeditionDraftPreparationMessage(shipment)}
                          </p>
                          {showGenerate ? (
                            <button
                              type="button"
                              onClick={openGenerateExpeditionConfirmation}
                              disabled={generatingShippingOrder}
                              className="px-3 py-2 text-sm font-medium rounded-md bg-indigo-700 text-white hover:bg-indigo-800 disabled:opacity-60"
                            >
                              {generatingShippingOrder
                                ? 'Génération…'
                                : 'Générer l’expédition'}
                            </button>
                          ) : null}
                        </div>
                      );
                    })()}
                    {canShowExpeditionLabelActions(commande) ? (
                      <div className="rounded-lg p-3 border border-emerald-200 bg-emerald-50 space-y-2">
                        <p className="text-xs font-bold uppercase tracking-wide text-emerald-800">
                          Étiquette
                        </p>
                        <p className="text-sm font-semibold text-emerald-900">
                          {expeditionLabelUi.loading
                            ? 'Vérification…'
                            : expeditionLabelStatusMessage({
                                labelStatus: expeditionLabelUi.status,
                                format: expeditionLabelUi.format,
                                polling: expeditionLabelPolling,
                              }) || 'Étiquette en cours de génération'}
                        </p>
                        <div className="flex flex-wrap gap-2">
                          <button
                            type="button"
                            onClick={() =>
                              refreshExpeditionLabelStatus(commande.shipment.id)
                            }
                            disabled={expeditionLabelUi.loading || expeditionLabelUi.downloading}
                            className="px-3 py-2 text-sm font-medium rounded-md border border-emerald-700 text-emerald-800 bg-white hover:bg-emerald-100 disabled:opacity-60"
                          >
                            Actualiser
                          </button>
                          {expeditionLabelUi.status === 'available' ? (
                            <button
                              type="button"
                              onClick={() =>
                                handleDownloadExpeditionLabel(
                                  commande.shipment.id,
                                  commande.id,
                                )
                              }
                              disabled={expeditionLabelUi.downloading || expeditionLabelUi.loading}
                              className="px-3 py-2 text-sm font-medium rounded-md bg-emerald-700 text-white hover:bg-emerald-800 disabled:opacity-60"
                            >
                              {expeditionLabelUi.downloading
                                ? 'Téléchargement…'
                                : 'Télécharger l’étiquette'}
                            </button>
                          ) : null}
                        </div>
                        {expeditionLabelUi.format ? (
                          <p className="text-xs text-emerald-700">{expeditionLabelUi.format}</p>
                        ) : null}
                      </div>
                    ) : null}
                    {canShowExpeditionMerchantHandoffButton(commande) ? (
                      <div className="rounded-lg p-3 border border-amber-200 bg-amber-50">
                        <p className="text-xs font-bold uppercase tracking-wide text-amber-800 mb-2">
                          Remise transporteur
                        </p>
                        <button
                          type="button"
                          onClick={openExpeditionHandoffConfirmation}
                          disabled={merchantHandoffSubmitting}
                          className="px-3 py-2 text-sm font-medium rounded-md bg-amber-700 text-white hover:bg-amber-800 disabled:opacity-60"
                        >
                          {merchantHandoffSubmitting
                            ? 'Enregistrement…'
                            : 'Colis remis au transporteur'}
                        </button>
                      </div>
                    ) : null}
                    {canShowExpeditionTrackingPanel(commande) ? (
                      <div className="rounded-lg p-3 border border-sky-200 bg-sky-50 space-y-3">
                        <p className="text-xs font-bold uppercase tracking-wide text-sky-800">
                          Suivi du colis
                        </p>
                        <div className="grid gap-2 sm:grid-cols-2">
                          <div>
                            <p className="text-xs font-semibold text-sky-700 uppercase tracking-wide">
                              Transporteur
                            </p>
                            <p className="text-sm font-semibold text-sky-950">
                              {commande.shipment?.carrier?.trim() || '—'}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs font-semibold text-sky-700 uppercase tracking-wide">
                              Statut du colis
                            </p>
                            <p className="text-sm font-semibold text-sky-950">
                              {expeditionShipmentStatusLabel(commande.shipment?.status)}
                            </p>
                          </div>
                          <div className="sm:col-span-2">
                            <p className="text-xs font-semibold text-sky-700 uppercase tracking-wide">
                              Numéro de suivi
                            </p>
                            <p className="text-sm font-mono text-sky-950 break-all">
                              {commande.shipment?.trackingNumber?.trim() || '—'}
                            </p>
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {canSyncExpeditionTracking(commande) ? (
                            <button
                              type="button"
                              onClick={handleSyncExpeditionTracking}
                              disabled={expeditionTrackingSyncing}
                              className="px-3 py-2 text-sm font-medium rounded-md bg-sky-700 text-white hover:bg-sky-800 disabled:opacity-60"
                            >
                              {expeditionTrackingSyncing ? 'Actualisation…' : 'Actualiser le suivi'}
                            </button>
                          ) : null}
                          {commande.shipment?.trackingUrl ? (
                            <a
                              href={commande.shipment.trackingUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="px-3 py-2 text-sm font-medium rounded-md border border-sky-700 text-sky-800 bg-white hover:bg-sky-100"
                            >
                              {expeditionCarrierFollowLinkLabel(commande.shipment?.carrier)}
                            </a>
                          ) : null}
                        </div>
                      </div>
                    ) : null}
                    <div>
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                        Adresse de destination
                      </p>
                      <p className="text-sm text-gray-900 whitespace-pre-wrap">
                        {commande.adresseLivraison?.trim()
                          || ([
                                commande.shipment?.recipientStreet,
                                [
                                  commande.shipment?.recipientPostalCode,
                                  commande.shipment?.recipientCity,
                                ]
                                  .filter(Boolean)
                                  .join(' '),
                                commande.shipment?.recipientCountry,
                              ]
                                .filter(Boolean)
                                .join('\n')
                            || 'Adresse non renseignée')}
                      </p>
                    </div>
                    {(() => {
                      const contactName =
                        commande.shipment?.recipientName
                        || [commande.user?.prenom, commande.user?.nom]
                          .filter(Boolean)
                          .join(' ')
                          .trim()
                        || null;
                      const contactPhone =
                        commande.shipment?.recipientPhone?.trim()
                        || commande.user?.telephone?.trim()
                        || null;
                      const contactEmail =
                        commande.shipment?.recipientEmail?.trim()
                        || commande.user?.email?.trim()
                        || null;
                      if (!contactName && !contactPhone && !contactEmail) return null;
                      return (
                        <div>
                          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                            Contact
                          </p>
                          {contactName ? (
                            <p className="text-sm text-gray-900">{contactName}</p>
                          ) : null}
                          {contactPhone ? (
                            <p className="text-sm text-gray-900">{contactPhone}</p>
                          ) : null}
                          {contactEmail ? (
                            <p className="text-sm text-gray-600">{contactEmail}</p>
                          ) : null}
                        </div>
                      );
                    })()}
                    {Number(commande.fraisExpedition) > 0 && (
                      <p className="text-sm text-gray-600">
                        Frais d&apos;expédition: {formatEuro(Number(commande.fraisExpedition))}
                      </p>
                    )}
                  </div>
                ) : (
                  <div className="space-y-3">
                    {(() => {
                      const livraisonDisplay = resolveLivraisonCommandeDisplay(commande);
                      if (!livraisonDisplay) return null;
                      const programmed = livraisonDisplay.kind === 'programmee';
                      return (
                        <div
                          className={`rounded-lg p-3 ${
                            programmed
                              ? 'border-2 border-amber-400 bg-amber-50'
                              : 'border border-green-200 bg-green-50'
                          }`}
                        >
                          <p className="text-xs font-bold uppercase tracking-wide text-gray-700">
                            {programmed ? 'Livraison programmée' : 'Livraison'}
                          </p>
                          {programmed ? (
                            <>
                              {livraisonDisplay.dateLabel ? (
                                <p className="text-sm font-semibold text-amber-900 mt-1">
                                  {livraisonDisplay.dateLabel}
                                </p>
                              ) : null}
                              {livraisonDisplay.plageLabel ? (
                                <p className="text-sm font-semibold text-amber-900">
                                  {livraisonDisplay.plageLabel}
                                </p>
                              ) : null}
                            </>
                          ) : (
                            <>
                              <p className="text-sm font-semibold text-green-900 mt-1">
                                Dès que possible
                              </p>
                              {livraisonDisplay.estimationHint ? (
                                <p className="text-sm text-green-800">
                                  {livraisonDisplay.estimationHint}
                                </p>
                              ) : null}
                            </>
                          )}
                        </div>
                      );
                    })()}
                    <div>
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                        Adresse de livraison
                      </p>
                      <p className="text-sm text-gray-900 whitespace-pre-wrap">
                        {commande.adresseLivraison?.trim() || 'Adresse non renseignée'}
                      </p>
                    </div>
                    {([commande.user?.prenom, commande.user?.nom]
                      .filter(Boolean)
                      .join(' ')
                      .trim() || null) && (
                      <div>
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                          Client
                        </p>
                        <p className="text-sm text-gray-900">
                          {[commande.user?.prenom, commande.user?.nom]
                            .filter(Boolean)
                            .join(' ')}
                        </p>
                      </div>
                    )}
                    {commande.user?.telephone?.trim() && (
                      <div>
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                          Téléphone
                        </p>
                        <p className="text-sm text-gray-900">
                          {commande.user.telephone.trim()}
                        </p>
                      </div>
                    )}
                    {commande.fraisLivraison > 0 && (
                      <p className="text-sm text-gray-600">
                        Frais de livraison: {formatEuro(commande.fraisLivraison)}
                      </p>
                    )}
                  </div>
                )}
              </div>

              {commande.modeRetrait === 'livraison' && !isRendezVous(commande) && (
                <div className="border rounded-lg p-4 bg-purple-50">
                  <h4 className="font-semibold mb-3 text-gray-700 flex items-center gap-2">
                    <Truck className="h-4 w-4" />
                    Livreur
                  </h4>
                  {livreurFigee ? (
                    <div className="text-sm space-y-3">
                      {hasAssignedLivreur ? (
                        <>
                          <div>
                            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                              {commande.statut === 'LIVREE' ? 'Livré par' : 'Livreur'}
                            </p>
                            <p className="font-medium text-gray-900 text-base">
                              {livreurNom || (commande.livreurId ? 'Chargement…' : '—')}
                            </p>
                            {assignedLivreur?.telephone && (
                              <p className="text-gray-600 mt-0.5">{assignedLivreur.telephone}</p>
                            )}
                          </div>
                          {commande.statut === 'LIVREE' && (
                            <div className="p-3 bg-white rounded-lg border border-purple-100">
                              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                                Commentaire à la livraison
                              </p>
                              {commande.commentaireLivreur?.trim() ? (
                                <p className="text-gray-700 whitespace-pre-wrap">
                                  {commande.commentaireLivreur.trim()}
                                </p>
                              ) : (
                                <p className="text-sm text-gray-400 italic">
                                  Aucun commentaire laissé par le livreur
                                </p>
                              )}
                            </div>
                          )}
                        </>
                      ) : (
                        <p className="text-gray-600">Aucun livreur n&apos;était assigné</p>
                      )}
                    </div>
                  ) : (
                    <>
                  {hasAssignedLivreur ? (
                    <div className="mb-3 text-sm">
                      <p className="font-medium">
                        {livreurNom || 'Livreur assigné'}
                      </p>
                      {assignedLivreur?.telephone && (
                        <p className="text-gray-600">{assignedLivreur.telephone}</p>
                      )}
                      {assignedLivreur?.statut && (
                        <p className="text-gray-500 capitalize">Statut : {assignedLivreur.statut}</p>
                      )}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-600 mb-3">Aucun livreur assigné</p>
                  )}
                  {!hasAssignedLivreur && livreursDisponibles.length === 0 ? (
                    <p className="text-sm text-amber-700">
                      Aucun livreur actif pour ce commerce. Créez-en un dans l&apos;onglet Livreurs.
                    </p>
                  ) : livreursDisponibles.length > 0 ? (
                    <div className="space-y-2">
                      <p className="text-xs text-gray-500">
                        Pré-assigner un livreur n&apos;avance pas le statut — utilisez le bouton « Confirmer » en bas.
                      </p>
                      <div className="flex flex-col sm:flex-row gap-2">
                      <select
                        value={selectedLivreurId}
                        onChange={(e) => setSelectedLivreurId(e.target.value)}
                        className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
                      >
                        <option value="">
                          {hasAssignedLivreur ? 'Changer de livreur' : 'Choisir un livreur'}
                        </option>
                        {livreursDisponibles.map((l) => (
                          <option key={l.id} value={l.id}>
                            {l.prenom} {l.nom}
                            {l.disponible === false ? ' (occupé)' : ''}
                          </option>
                        ))}
                      </select>
                      <button
                        type="button"
                        disabled={!selectedLivreurId || assigningLivreur}
                        onClick={() => handleAssignerLivreur(false)}
                        className="px-4 py-2 bg-purple-700 text-white text-sm font-medium rounded-lg hover:bg-purple-800 disabled:opacity-50"
                      >
                        {assigningLivreur ? 'Assignation…' : hasAssignedLivreur ? 'Réassigner' : 'Assigner'}
                      </button>
                      </div>
                    </div>
                  ) : null}
                    </>
                  )}
                </div>
              )}

              <div className="border rounded-lg p-4">
                <h4 className="font-semibold mb-3 text-gray-700">🍽️ Produits commandés</h4>
                {(() => {
                  const refundInfo = {};
                  (commande.paiements || []).forEach((p) => {
                    if (p.statutPaiement !== 'REMBOURSE' || p.montant >= 0) return;
                    const meta = parseSavReference(p.reference);
                    const ligneIds = meta?.ligneIds;
                    if (!Array.isArray(ligneIds) || ligneIds.length === 0) return;
                    const isComplet = meta?.type === 'REMBOURSEMENT_COMPLET';
                    ligneIds.forEach((id) => {
                      if (!refundInfo[id]) refundInfo[id] = { qty: 0, montant: 0, isComplet };
                      const qtyFromMeta = meta?.ligneQuantites?.[id];
                      if (qtyFromMeta) refundInfo[id].qty += qtyFromMeta;
                      else refundInfo[id].isComplet = true;
                    });
                    if (ligneIds.length === 1) {
                      const onlyId = ligneIds[0];
                      refundInfo[onlyId].montant += Math.abs(p.montant);
                    } else if (ligneIds.length > 1 && meta?.ligneQuantites) {
                      ligneIds.forEach((id) => {
                        const line = commande.produits?.find((lp) => lp.id === id);
                        if (line && meta.ligneQuantites[id]) {
                          const unitPrice =
                            (line.prixTotal || line.prixUnitaire * line.quantite) / line.quantite;
                          refundInfo[id].montant += unitPrice * meta.ligneQuantites[id];
                        }
                      });
                    }
                  });
                  const poidsActif = Boolean(
                    commande.commercant?.ajustementPoidsReelActif,
                  );
                  const statutAjustable = [
                    'EN_ATTENTE',
                    'CONFIRMEE',
                    'EN_PREPARATION',
                  ].includes(commande.statut);
                  const hasVariableWeight = commandeHasVariableWeightLines(
                    commande,
                  );
                  // Poids réel : autorisé même si commande online déjà encaissée
                  const canAdjustWeight =
                    poidsActif && statutAjustable && hasVariableWeight;
                  const statutLigneIndisponible = STATUTS_LIGNE_INDISPONIBLE.includes(
                    commande.statut,
                  );
                  const sousTotalOriginalPrix = Math.round(
                    (commande.produits || []).reduce(
                      (s, p) =>
                        s + (Number(p.prixFinal ?? p.prixTotal) || 0),
                      0
                    ) * 100
                  ) / 100;
                  const reductionOrigPrix =
                    Number(commande.reduction) || 0;
                  return (
                    <div className="space-y-2">
                      {indisponibleMessage && (
                        <p className="text-sm text-green-700 break-all">
                          {indisponibleMessage}
                        </p>
                      )}
                      {priceLockedOnline && !canAdjustWeight && (
                        <p className="text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                          {PRICE_LOCK_MESSAGE}
                        </p>
                      )}
                      {priceLockedOnline && canAdjustWeight && (
                        <p className="text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                          {VARIABLE_WEIGHT_LOCK_EXCEPTION_HINT}
                        </p>
                      )}
                      {commande.produits?.map((item, index) => {
                        const info = refundInfo[item.id];
                        const prixAffiche = resolveLignePrixPromo(item, {
                          reductionOrig: reductionOrigPrix,
                          sousTotalOriginal: sousTotalOriginalPrix,
                        });
                        const draft = poidsDrafts[item.id] || {};
                        const draftPrix =
                          draft.prixEstime != null && draft.prixEstime !== ''
                            ? Number(draft.prixEstime)
                            : null;
                        const lineTotal =
                          Number.isFinite(draftPrix) && draftPrix > 0
                            ? draftPrix
                            : prixAffiche.brut;
                        const isFullLine = info && (info.isComplet || info.qty >= item.quantite);
                        const isPartial = info && !isFullLine;
                        const isIndisponible = item.indisponibleAt != null;
                        const isMuted = isFullLine || isIndisponible;
                        const isPoids = isVariableWeightLine(item);
                        const canMarkIndisponible =
                          !isIndisponible &&
                          statutLigneIndisponible &&
                          !isRendezVous(commande) &&
                          !priceLockedOnline;
                        return (
                          <div key={item.id || index} className={`py-2 border-b last:border-b-0 ${info ? 'bg-orange-50 rounded-lg px-3 -mx-2' : ''} ${isIndisponible && !info ? 'bg-gray-50 rounded-lg px-3 -mx-2' : ''}`}>
                            <div className="flex justify-between items-start gap-3">
                              <div className="flex-1 pr-3 min-w-0">
                                <div className="flex flex-wrap items-center gap-2">
                                  <p className={`font-medium ${isMuted ? 'line-through text-gray-400' : ''}`}>
                                    {getLigneDesignation(item)}
                                  </p>
                                  {isIndisponible && (
                                    <span className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-semibold bg-gray-200 text-gray-700">
                                      Indisponible
                                    </span>
                                  )}
                                </div>
                                <p className={`text-sm ${isIndisponible ? 'text-gray-400' : 'text-gray-600'}`}>
                                  Quantité : {item.quantite}
                                </p>
                                {isPoids && (
                                  <p className="text-xs text-amber-700 mt-1">
                                    Souhaité : {item.poidsSouhaite || '—'} g
                                    {item.typeDecoupe ? ` · ${item.typeDecoupe}` : ''}
                                    {item.poidsReel ? ` · Réel : ${item.poidsReel} g` : ''}
                                  </p>
                                )}
                                <CommandeLigneDetails
                                  className="mt-2"
                                  commentaire={item.commentaire}
                                  options={item.options}
                                />
                                {canAdjustWeight && isPoids && !isIndisponible && (
                                  <div className="mt-2 grid grid-cols-2 gap-2 max-w-md">
                                    <div>
                                      <label className="block text-[11px] text-gray-500 mb-0.5">
                                        Poids réel (g)
                                      </label>
                                      <input
                                        type="number"
                                        min="1"
                                        step="1"
                                        value={draft.poidsReel ?? ''}
                                        onChange={(e) => {
                                          const poidsReel = e.target.value;
                                          const estim = estimerPrixDepuisPoids(item, poidsReel);
                                          setPoidsDrafts((prev) => ({
                                            ...prev,
                                            [item.id]: {
                                              ...prev[item.id],
                                              poidsReel,
                                              prixEstime:
                                                estim != null
                                                  ? estim
                                                  : prev[item.id]?.prixEstime ?? '',
                                            },
                                          }));
                                        }}
                                        className="w-full px-2 py-1.5 border border-amber-300 rounded-md text-sm"
                                      />
                                    </div>
                                    <div>
                                      <label className="block text-[11px] text-gray-500 mb-0.5">
                                        Prix recalculé (€)
                                      </label>
                                      <input
                                        type="text"
                                        readOnly
                                        value={
                                          draft.prixEstime != null && draft.prixEstime !== ''
                                            ? Number(draft.prixEstime).toFixed(2)
                                            : '—'
                                        }
                                        className="w-full px-2 py-1.5 border border-gray-200 rounded-md text-sm bg-gray-50 text-gray-700"
                                        title="Calcul serveur : poids réel × prix au poids (indicatif)"
                                      />
                                    </div>
                                  </div>
                                )}
                                {canMarkIndisponible && (
                                  <button
                                    type="button"
                                    onClick={() => openLigneIndisponibleConfirmation(item)}
                                    disabled={markingIndisponible}
                                    className="mt-2 text-xs font-medium text-gray-600 hover:text-red-700 underline-offset-2 hover:underline disabled:opacity-50"
                                  >
                                    Article indisponible
                                  </button>
                                )}
                              </div>
                              <div className="shrink-0 text-right">
                                {prixAffiche.hasPromo ? (
                                  <>
                                    <span
                                      className={`block text-sm ${
                                        isMuted
                                          ? 'line-through text-gray-400'
                                          : 'line-through text-gray-400'
                                      }`}
                                    >
                                      {formatEuro(prixAffiche.brut)}
                                    </span>
                                    <span
                                      className={`block font-semibold ${
                                        isMuted ? 'line-through text-gray-400' : 'text-red-600'
                                      }`}
                                    >
                                      {formatEuro(prixAffiche.net)}
                                    </span>
                                  </>
                                ) : (
                                  <span
                                    className={`font-semibold ${
                                      isMuted ? 'line-through text-gray-400' : ''
                                    } ${
                                      Number.isFinite(draftPrix) &&
                                      draftPrix > 0 &&
                                      Math.abs(draftPrix - prixAffiche.brut) > 0.005
                                        ? 'text-amber-700'
                                        : ''
                                    }`}
                                  >
                                    {formatEuro(Number(lineTotal || 0))}
                                  </span>
                                )}
                              </div>
                            </div>
                            {info && (
                              <div className="mt-1.5 flex items-center justify-between bg-orange-100 rounded-md px-2.5 py-1.5">
                                <span className="text-xs font-semibold text-orange-700">
                                  {isFullLine
                                    ? 'Remboursé'
                                    : info.qty > 0
                                      ? `Remboursé : ${info.qty} sur ${item.quantite}`
                                      : 'Remboursement partiel'}
                                </span>
                                <span className="text-xs font-bold text-orange-800">
                                  -{formatEuro((info.montant || lineTotal))}
                                </span>
                              </div>
                            )}
                          </div>
                        );
                      })}
                      {poidsActif &&
                        canAdjustWeight &&
                        Object.keys(poidsDrafts).length > 0 && (
                          <div className="pt-3 space-y-2">
                            {poidsError && (
                              <p className="text-sm text-red-600">{poidsError}</p>
                            )}
                            {poidsMessage && (
                              <p className="text-sm text-green-700">{poidsMessage}</p>
                            )}
                            {poidsComplementUrl && (
                              <button
                                type="button"
                                className="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-md bg-sky-600 text-white hover:bg-sky-700"
                                onClick={() =>
                                  window.open(
                                    poidsComplementUrl,
                                    '_blank',
                                    'noopener,noreferrer'
                                  )
                                }
                              >
                                Ouvrir le lien de complément
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={handleSavePoidsAjustement}
                              disabled={savingPoids}
                              className="px-4 py-2 bg-amber-600 text-white text-sm font-medium rounded-md hover:bg-amber-700 disabled:opacity-60"
                            >
                              {savingPoids
                                ? 'Enregistrement…'
                                : 'Enregistrer poids réel & ajuster le total'}
                            </button>
                          </div>
                        )}
                    </div>
                  );
                })()}
              </div>

              <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                {(() => {
                  const lignes = commande.produits || [];
                  const lignesActives = lignes.filter(
                    (p) => p.indisponibleAt == null
                  );
                  const lineAmount = (p) =>
                    Number(p.prixFinal ?? p.prixTotal ?? 0) || 0;
                  const sousTotalActif =
                    Math.round(
                      lignesActives.reduce((sum, p) => sum + lineAmount(p), 0) *
                        100
                    ) / 100;
                  const sousTotalOriginal =
                    Math.round(
                      lignes.reduce((sum, p) => sum + lineAmount(p), 0) * 100
                    ) / 100;
                  const isExpeditionMode = commande.modeRetrait === 'expedition';
                  const frais = isExpeditionMode
                    ? Number(commande.fraisExpedition) || 0
                    : Number(commande.fraisLivraison) || 0;
                  const reducBrute = Number(commande.reduction) || 0;
                  const reducProportionnelle =
                    sousTotalOriginal > 0
                      ? Math.round(
                          ((reducBrute * sousTotalActif) / sousTotalOriginal) *
                            100
                        ) / 100
                      : reducBrute;
                  const reducEffective =
                    Math.round(
                      Math.min(
                        reducProportionnelle,
                        Math.max(0, sousTotalActif + frais)
                      ) * 100
                    ) / 100;
                  const refunds = (commande.paiements || []).filter(
                    (p) => p.statutPaiement === 'REMBOURSE' && p.montant < 0
                  );
                  const totalRefund = refunds.reduce(
                    (s, p) => s + Math.abs(p.montant),
                    0
                  );
                  const {
                    orderTotal,
                    hasRefund,
                    totalRefund: refundShown,
                    netTotal,
                    strikeTotal,
                  } = resolveCommandeDetailTotals({
                    total: commande.total,
                    totalRefund,
                    netFromPayments:
                      getCommandeSoldeRemboursable(commande),
                  });
                  return (
                    <>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Sous-total</span>
                        <span>{formatEuro(sousTotalActif)}</span>
                      </div>
                      {frais > 0 && (
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">
                            {isExpeditionMode ? "Frais d'expédition" : 'Frais de livraison'}
                          </span>
                          <span>{formatEuro(frais)}</span>
                        </div>
                      )}
                      {(() => {
                        const { parts, total: partsTotal } =
                          buildReductionDisplayLines(commande);
                        const scale =
                          reducBrute > 0 ? reducEffective / reducBrute : 1;
                        const scaledParts = parts.map((p) => ({
                          ...p,
                          amount:
                            Math.round(Number(p.amount) * scale * 100) / 100,
                        }));
                        const scaledTotal =
                          Math.round(
                            (partsTotal > 0
                              ? partsTotal * scale
                              : reducEffective) * 100
                          ) / 100;
                        return (
                          <>
                            {scaledParts.map((part) =>
                              part.amount > 0 ? (
                                <div
                                  key={part.key}
                                  className="flex justify-between text-sm text-green-600"
                                >
                                  <span>{part.label}</span>
                                  <span>-{formatEuro(part.amount)}</span>
                                </div>
                              ) : null
                            )}
                            {scaledParts.length > 1 && scaledTotal > 0 && (
                              <div className="flex justify-between text-sm text-green-700 font-medium">
                                <span>Total réductions</span>
                                <span>-{formatEuro(scaledTotal)}</span>
                              </div>
                            )}
                            {scaledParts.length === 0 && reducEffective > 0 && (
                              <div className="flex justify-between text-sm text-green-600">
                                <span>Réduction</span>
                                <span>-{formatEuro(reducEffective)}</span>
                              </div>
                            )}
                          </>
                        );
                      })()}
                      <div
                        className={`flex justify-between text-lg font-bold pt-2 border-t ${
                          strikeTotal ? 'text-gray-400' : ''
                        }`}
                      >
                        <span>Total</span>
                        <span
                          className={
                            strikeTotal
                              ? 'line-through text-gray-400'
                              : 'text-primary-600'
                          }
                        >
                          {formatEuro(orderTotal)}
                        </span>
                      </div>
                      {hasRefund && (
                        <>
                          <div className="flex justify-between text-sm text-orange-600">
                            <span>Remboursé ({refunds.length})</span>
                            <span>-{formatEuro(refundShown)}</span>
                          </div>
                          <div className="flex justify-between text-lg font-bold">
                            <span>Total net</span>
                            <span
                              className={
                                netTotal <= 0
                                  ? 'text-red-600'
                                  : 'text-primary-600'
                              }
                            >
                              {formatEuro(netTotal)}
                            </span>
                          </div>
                        </>
                      )}
                    </>
                  );
                })()}
              </div>

              {Array.isArray(commande.modifications) &&
                commande.modifications.length > 0 && (
                  <div className="border rounded-lg p-4">
                    <h4 className="font-semibold mb-3 text-gray-700">Modifications</h4>
                    <ul className="space-y-3">
                      {commande.modifications.map((mod) => (
                          <li
                            key={mod.id}
                            className="text-sm border-b border-gray-100 last:border-0 pb-2 last:pb-0"
                          >
                            <div className="text-gray-500 text-xs">
                              {formatDateTime(mod.createdAt) || '—'}
                            </div>
                            <p className="font-medium text-gray-800 mt-0.5">
                              {mod.type === 'LIGNE_INDISPONIBLE'
                                ? 'Article indisponible'
                                : mod.type}
                              {mod.designation ? ` — ${mod.designation}` : ''}
                            </p>
                            <p className="text-gray-600 text-xs mt-0.5">
                              {mod.quantite != null ? `Qté ${mod.quantite} · ` : ''}
                              Total {formatEuro(Number(mod.totalAvant ?? 0))} →{' '}
                              {formatEuro(Number(mod.totalApres ?? 0))}
                              {mod.montantLigneTTC != null
                                ? ` (−${formatEuro(Number(mod.montantLigneTTC))})`
                                : ''}
                            </p>
                          </li>
                        ))}
                    </ul>
                  </div>
                )}

              <div className="border rounded-lg p-4">
                <h4 className="font-semibold mb-2 text-gray-700">💳 Paiement</h4>
                {(() => {
                  const rdvPay = isRendezVous(commande)
                    ? getRdvPaymentView(commande)
                    : null;
                  if (rdvPay) {
                    return (
                      <div className="space-y-1 text-sm">
                        <p>
                          Mode : <span className="font-medium">{rdvPay.modeLabel}</span>
                          {rdvPay.isOnline ? (
                            <span className="ml-2 text-[#635BFF] font-medium">Stripe</span>
                          ) : null}
                        </p>
                        <p>
                          Prix total : <span className="font-medium">{formatEuro(rdvPay.total)}</span>
                        </p>
                        <p>
                          {rdvPay.isRefunded
                            ? 'Montant actuellement encaissé'
                            : rdvPay.mode === 'DEPOSIT_FIXED'
                              ? 'Arrhes payées en ligne'
                              : 'Payé en ligne'}{' '}
                          : <span className="font-medium">{formatEuro(rdvPay.amountPaid)}</span>
                        </p>
                        {!rdvPay.isRefunded ? (
                          <p>
                            Reste à payer :{' '}
                            <span className="font-medium">{formatEuro(rdvPay.amountDueLater)}</span>
                          </p>
                        ) : null}
                        <p>
                          Statut :{' '}
                          <span className="font-medium">
                            {rdvPay.summaryLabel || rdvPay.paymentStatus || '—'}
                          </span>
                        </p>
                      </div>
                    );
                  }
                  const fin = getCommandeFinancier(commande);
                  const tenders = buildTenderBreakdown(commande?.paiements || [], {
                    modePaiement: commande?.modePaiement,
                  });
                  const remboursementsMontant = (commande.paiements || []).filter(
                    (p) => p.statutPaiement === 'REMBOURSE' && p.montant < 0
                  );
                  return (
                    <>
                      {tenders.lines.length > 0 ? (
                        <div className="space-y-1">
                          {tenders.lines.map((l) => (
                            <p key={l.methode} className="text-sm flex justify-between gap-4">
                              <span>{l.label}</span>
                              <span className="font-medium tabular-nums">{formatEuro(l.amount)}</span>
                            </p>
                          ))}
                          <p className="text-sm flex justify-between gap-4 border-t pt-1 mt-1">
                            <span className="font-medium">Payé</span>
                            <span className="font-medium tabular-nums">
                              {formatEuro(tenders.totalPaid)}
                            </span>
                          </p>
                        </div>
                      ) : (
                        <p className="text-sm">
                          Mode:{' '}
                          {commande.modePaiement === 'surplace' ||
                          commande.methodePaiement === 'ESPECES' ? (
                            <span className="font-medium">Sur place</span>
                          ) : (
                            <span className="font-medium">
                              {commande.modePaiement || 'Non spécifié'}
                            </span>
                          )}
                        </p>
                      )}
                      <p className="text-sm mt-2">
                        Total commande :{' '}
                        <strong className="tabular-nums">{formatEuro(fin.totalCommande)}</strong>
                      </p>
                      <p className="text-sm mt-2">
                        Statut:{' '}
                        <span
                          className={`font-medium ${
                            fin.isRembourseTotal
                              ? 'text-orange-700'
                              : fin.hasResteAEncaisser
                                ? 'text-sky-700'
                                : fin.isPartiellementRembourse
                                  ? 'text-amber-700'
                                  : commande.statutPaiement === 'PAYE' ||
                                      commande.statutPaiement === 'VALIDE'
                                    ? 'text-green-600'
                                    : 'text-orange-600'
                          }`}
                        >
                          {fin.isRembourseTotal
                            ? 'Remboursé'
                            : fin.hasResteAEncaisser
                              ? 'Complément à encaisser'
                              : fin.isPartiellementRembourse
                                ? 'Partiellement remboursé'
                                : commande.statutPaiement === 'VALIDE'
                                  ? 'Payé'
                                  : commande.statutPaiement}
                        </span>
                      </p>
                      {(fin.isPartiellementRembourse ||
                        fin.montantRembourse > 0.01 ||
                        fin.hasResteAEncaisser) && (
                        <div className="text-sm text-gray-800 mt-1 space-y-0.5">
                          <p>
                            Net encaissé :{' '}
                            <strong className="tabular-nums">{formatEuro(fin.netEncaisse)}</strong>
                          </p>
                          {fin.montantRembourse > 0.01 && (
                            <p className="text-amber-800">
                              Remboursé :{' '}
                              <span className="tabular-nums">{formatEuro(fin.montantRembourse)}</span>
                            </p>
                          )}
                          {fin.hasResteAEncaisser && (
                            <p className="text-sky-800">
                              Reste à encaisser :{' '}
                              <strong className="tabular-nums">
                                {formatEuro(fin.resteAEncaisser)}
                              </strong>
                            </p>
                          )}
                        </div>
                      )}
                      {(() => {
                        const complementUrl =
                          poidsComplementUrl ||
                          getPendingComplementCheckoutUrl(commande.paiements || []);
                        if (!complementUrl || !fin.hasResteAEncaisser) return null;
                        return (
                          <button
                            type="button"
                            className="mt-2 inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-md bg-sky-600 text-white hover:bg-sky-700"
                            onClick={() => window.open(complementUrl, '_blank', 'noopener,noreferrer')}
                          >
                            Ouvrir le lien de complément
                          </button>
                        );
                      })()}
                      {remboursementsMontant.length > 0 && (
                        <ul className="mt-2 space-y-1 text-xs text-gray-600 border-t pt-2">
                          {remboursementsMontant.map((p) => {
                            const meta = parseSavReference(p.reference);
                            const libelle =
                              meta?.type === 'REMBOURSEMENT_COMPLET'
                                ? 'Remboursement total'
                                : meta?.type === 'REMBOURSEMENT_LIBRE'
                                  ? 'Remboursement partiel'
                                  : 'Remboursement';
                            return (
                              <li key={p.id} className="flex justify-between">
                                <span>{libelle}</span>
                                <span className="font-medium text-orange-700">
                                  {formatEuro(Number(p.montant))}
                                </span>
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </>
                  );
                })()}
              </div>

              <div className="border-t pt-4">
                <h4 className="font-semibold mb-3 text-gray-700">Actions</h4>
                <div className="grid grid-cols-2 gap-3">
                  {detailNextStatus && commande.statut !== 'ANNULEE' && commande.statut !== 'LIVREE' && (
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (detailNextStatus) {
                          openStatusConfirmation(commande, detailNextStatus);
                        }
                      }}
                      disabled={isUpdating || !detailNextStatus}
                      type="button"
                      className="flex items-center justify-center bg-green-600 text-white px-4 py-3 rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <CheckCircle className="h-5 w-5 mr-2" />
                      {getNextStatusLabel(commande.statut, commande.modeRetrait, commande)}
                    </button>
                  )}

                  {!detailIsPosComptoir && commande.statut !== 'ANNULEE' && commande.statut !== 'LIVREE' && (
                    <button
                      onClick={() =>
                        openStatusConfirmation(commande, 'ANNULEE', {
                          afterConfirm: onClose,
                        })
                      }
                      disabled={isUpdating}
                      className="flex items-center justify-center bg-red-600 text-white px-4 py-3 rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <XCircle className="h-5 w-5 mr-2" />
                      Annuler la commande
                    </button>
                  )}

                  {canRefundOnline && (
                    <button
                      type="button"
                      onClick={() => openRefundModal(commande)}
                      disabled={isUpdating || refundLoading}
                      className="col-span-2 flex items-center justify-center bg-orange-600 text-white px-4 py-3 rounded-lg hover:bg-orange-700 disabled:opacity-50"
                    >
                      <RotateCcw className="h-5 w-5 mr-2" />
                      {tendersDetail.isMixed || tendersDetail.hasGift
                        ? `Effectuer le remboursement total — ${formatEuro(soldeRemboursable)}`
                        : `Rembourser (Stripe) — solde ${formatEuro(soldeRemboursable)}`}
                    </button>
                  )}
                  {isFactureTelechargeable(commande) ? (
                    <div className="col-span-2 space-y-2">
                      <button
                        type="button"
                        onClick={handleDownloadFacture}
                        disabled={factureLoading}
                        className="w-full flex items-center justify-center bg-violet-700 text-white px-4 py-3 rounded-lg hover:bg-violet-800 disabled:opacity-50"
                      >
                        <Download className="h-5 w-5 mr-2" />
                        {factureLoading
                          ? 'Téléchargement…'
                          : commande.numeroFacture
                            ? `Facture ${commande.numeroFacture} — Télécharger`
                            : 'Télécharger la facture PDF'}
                      </button>
                      {(commande.avoirs || []).map((av) => (
                        <button
                          key={av.id}
                          type="button"
                          onClick={() => handleDownloadAvoir(av.id)}
                          disabled={avoirLoadingId === av.id}
                          className="w-full flex items-center justify-between bg-slate-700 text-white px-4 py-3 rounded-lg hover:bg-slate-800 disabled:opacity-50"
                        >
                          <span className="flex items-center">
                            <Download className="h-5 w-5 mr-2" />
                            Avoir {av.numeroAvoir}
                          </span>
                          <span className="tabular-nums">
                            {avoirLoadingId === av.id
                              ? '…'
                              : `-${formatEuro(Number(av.montantTTC || 0))}`}
                          </span>
                        </button>
                      ))}
                    </div>
                  ) : (
                    <p className="col-span-2 text-xs text-gray-600 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
                      Facture PDF disponible après livraison ou retrait (pas pour une commande
                      encore en cours sans facture émise).
                    </p>
                  )}
                  {isCommandePaiementEnLigne(commande) &&
                    commande.statutPaiement === 'VALIDE' &&
                    !detailIsPosComptoir &&
                    commande.canalVente !== 'POS' &&
                    !stripePiOk && (
                      <p className="col-span-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                        Remboursement Clickorico indisponible pour cette commande (historique sans référence
                        Stripe). Utilisez le dashboard Stripe, puis mettez à jour le statut si besoin.
                      </p>
                    )}
                </div>

                <button
                  onClick={onClose}
                  className="w-full mt-3 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Fermer
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
  );
}
